/**
 * Utilitários para normalização de texto
 * Remove acentos e caracteres diacríticos para melhorar buscas
 */

/**
 * Normaliza texto removendo acentos e caracteres diacríticos
 * @param text - Texto a ser normalizado
 * @returns Texto normalizado sem acentos
 */
export function normalizeText(text: string): string {
  if (!text || typeof text !== 'string') {
    return '';
  }
  
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, ''); // Remove acentos e diacríticos
}

/**
 * Normaliza texto para busca (remove acentos e converte para minúsculas)
 * @param text - Texto a ser normalizado para busca
 * @returns Texto normalizado para comparação
 */
export function normalizeForSearch(text: string): string {
  if (!text || typeof text !== 'string') {
    return '';
  }
  
  return normalizeText(text)
    .trim()
    .replace(/\s+/g, ' '); // Normaliza espaços múltiplos
}

/**
 * Verifica se um texto contém outro texto normalizado (sem acentos)
 * @param source - Texto fonte onde buscar
 * @param searchTerm - Termo a ser buscado
 * @returns true se o termo for encontrado
 */
export function containsNormalized(source: string, searchTerm: string): boolean {
  if (!source || !searchTerm) {
    return false;
  }
  
  const normalizedSource = normalizeForSearch(source);
  const normalizedSearch = normalizeForSearch(searchTerm);
  
  return normalizedSource.includes(normalizedSearch);
}

/**
 * Gera variações de um termo de busca com e sem acentos
 * @param searchTerm - Termo de busca original
 * @returns Array com variações do termo
 */
export function generateSearchVariations(searchTerm: string): string[] {
  if (!searchTerm || typeof searchTerm !== 'string') {
    return [];
  }
  
  const variations = [];
  
  // Adicionar o termo original
  variations.push(searchTerm);
  
  // SEMPRE adicionar o termo normalizado (sem acentos)
  const normalized = normalizeForSearch(searchTerm);
  if (normalized !== searchTerm) {
    variations.push(normalized);
  }
  
  // Gerar variações específicas para palavras comuns
  if (normalized === 'informatica') {
    variations.push('informática', 'informàtica', 'informãtica', 'informâtica', 'informätica');
  }
  
  if (normalized === 'tecnologia') {
    variations.push('tecnología', 'tecnològia', 'tecnolõgia', 'tecnolôgia', 'tecnolögia');
  }
  
  // Gerar variações com acentos para cada vogal
  const commonAccents: Record<string, string[]> = {
    'a': ['á', 'à', 'ã', 'â', 'ä'],
    'e': ['é', 'è', 'ê', 'ë'],
    'i': ['í', 'ì', 'î', 'ï'],
    'o': ['ó', 'ò', 'õ', 'ô', 'ö'],
    'u': ['ú', 'ù', 'û', 'ü'],
    'c': ['ç'],
    'n': ['ñ']
  };
  
  // Para cada caractere base com acentos, gerar variações
  for (const [base, accents] of Object.entries(commonAccents)) {
    if (normalized.includes(base)) {
      for (const accent of accents) {
        const variation = normalized.replace(new RegExp(base, 'gi'), accent);
        if (variation !== normalized && !variations.includes(variation)) {
          variations.push(variation);
        }
      }
    }
  }
  
  // Gerar variações combinando múltiplos acentos para palavras específicas
  if (normalized === 'informatica') {
    // Combinar acentos em diferentes posições
    variations.push('informáticá', 'informàticà', 'informãticã');
  }
  
  if (normalized === 'tecnologia') {
    // Combinar acentos em diferentes posições
    variations.push('tecnologiá', 'tecnològià', 'tecnolõgiã');
  }
  
  // Remover duplicatas mantendo a ordem
  return [...new Set(variations)];
}
